import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ArrowRight, X } from "lucide-react";
import Layout from "../components/Layout";
import { LOCATIONS } from "../data/content";
import { SERVICES } from "../data/services";

const nt = { target: "_blank", rel: "noopener noreferrer" };

const SERVICE_CARDS = [
  { slug: "interior", ...SERVICES.interior },
  { slug: "exterior", ...SERVICES.exterior },
  { slug: "full", ...SERVICES.full },
];

const EXPLORE_CARDS = [
  { title: "Deals & Gift Cards", sub: "Newest coupons, discounts & buy a gift card.", to: "/deals", img: "https://customer-assets-39nsmqrw.emergentagent.net/job_pink-crew-oahu/artifacts/ym6ekuwe_IMG_2268.webp" },
  { title: "Maintenance Plans", sub: "Recurring details up to 50% off with bonus add-ons.", to: "/maintenance", img: "https://customer-assets-39nsmqrw.emergentagent.net/job_pink-crew-oahu/artifacts/h3n0a9qu_IMG_2265.webp" },
  { title: "Reviews", sub: "See real 5-star client reviews and results.", to: "/reviews", img: "/explore/reviews.jpg" },
  { title: "Customizable Plan", sub: "Build your own plan and only pay for what you need.", to: "/custom-plan", img: "/explore/customizable.jpg" },
];

function BookPopup() {
  const [show, setShow] = useState(true);
  useEffect(() => { const t = setTimeout(() => setShow(false), 3400); return () => clearTimeout(t); }, []);
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: -40, scale: 0.85 }}
          animate={{ opacity: 1, y: 0, scale: 1, x: [0, -9, 9, -7, 7, -4, 4, 0] }}
          exit={{ opacity: 0, y: 320 }}
          transition={{ duration: 0.6, x: { delay: 0.55, duration: 0.6 } }}
          className="fixed top-24 left-1/2 -translate-x-1/2 z-[100] w-[90vw] max-w-sm"
          data-testid="book-popup"
        >
          <div className="relative rounded-2xl border border-pink-500/50 bg-gradient-to-br from-[#1a0710] to-[#121212] p-5 text-center shadow-2xl shadow-pink-600/30">
            <button onClick={() => setShow(false)} className="absolute top-3 right-3 text-zinc-400 hover:text-white" data-testid="book-popup-close"><X size={18} /></button>
            <div className="flex justify-center gap-1 mb-2">{[...Array(5)].map((_, i) => <Star key={i} size={15} className="fill-pink-500 text-pink-500" />)}</div>
            <h3 className="text-2xl font-extrabold tracking-tighter">Book a Detail Today</h3>
            <p className="text-zinc-400 text-xs mt-1">100+ details completed · showroom results</p>
            <Link to="/book" {...nt} onClick={() => setShow(false)} className="mt-3 inline-block bg-pink-600 hover:bg-pink-500 text-white font-bold rounded-full px-8 py-3 transition-colors active:scale-95" data-testid="popup-book-btn">Book a Detail →</Link>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const ADDON_CARDS = [
  { key: "fees", title: "Fees", sub: "Lifted-truck, outside-windward & disaster-detail fees.", img: "/addons/fees.jpg" },
  { key: "exterior", title: "Exterior Add-ons", sub: "Headlight restoration, iron removal, water spots & more.", img: "/addons/exterior.jpg" },
  { key: "interior", title: "Interior Add-ons", sub: "Ceramic coating, pet hair, biohazard & extraction.", img: "/addons/interior.jpg" },
  { key: "signature", title: "Signature Add-ons", sub: "Leather, sealants, clay decon & finishing touches.", img: "/addons/signature.jpg" },
];

export default function Home() {
  const scrollToServices = () => document.getElementById("services")?.scrollIntoView({ behavior: "smooth" });
  return (
    <Layout>
      <BookPopup />
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="pc-glow w-[500px] h-[500px] -top-40 -right-20" />
        <img src={LOCATIONS.windward.hero} alt="Luxury detailed car" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-[#050505]/40" />
        <div className="relative max-w-7xl mx-auto px-6 py-28 md:py-40">
          <motion.p initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} className="text-xs tracking-[0.3em] uppercase text-pink-500 font-bold mb-5">Windward Oahu · Mobile Detailing</motion.p>
          <motion.h1 initial={{opacity:0,y:24}} animate={{opacity:1,y:0}} transition={{delay:0.1}} className="text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter max-w-4xl leading-[1.05]">
            Luxury car detailing that comes <span className="text-pink-500">to your driveway.</span>
          </motion.h1>
          <motion.p initial={{opacity:0,y:24}} animate={{opacity:1,y:0}} transition={{delay:0.2}} className="mt-6 text-lg text-zinc-300 max-w-2xl leading-relaxed">
            Teenagers running our professional mobile detailing, bringing your vehicle back to showroom condition. We come to you! With 100+ details completed, Pink Crew is committed to making your car shine!
          </motion.p>
          <motion.div initial={{opacity:0,y:24}} animate={{opacity:1,y:0}} transition={{delay:0.3}} className="mt-10 flex flex-wrap gap-5">
            <Link to="/book" {...nt} className="bg-pink-600 hover:bg-pink-500 text-white font-extrabold rounded-full px-12 py-7 text-2xl md:text-3xl transition-colors active:scale-95 flex items-center gap-3 shadow-2xl shadow-pink-600/30" data-testid="hero-book-btn">Book a Detail <ArrowRight size={28} /></Link>
            <button onClick={scrollToServices} className="bg-transparent border-2 border-white/25 hover:border-white/60 text-white rounded-full px-12 py-7 text-2xl md:text-3xl font-bold transition-colors" data-testid="hero-services-btn">See Services</button>
          </motion.div>
          <div className="mt-12 flex flex-wrap gap-8 text-sm text-zinc-400">
            <span className="flex items-center gap-2"><Star size={18} className="text-pink-500 fill-pink-500" /> 100 details serviced</span>
            <span className="flex items-center gap-2"><Star size={18} className="text-pink-500 fill-pink-500" /> 5 star rating</span>
            <span className="flex items-center gap-2"><Star size={18} className="text-pink-500 fill-pink-500" /> Book in 60 seconds</span>
          </div>
        </div>
      </section>

      {/* Services (3 cards) */}
      <section id="services" className="max-w-7xl mx-auto px-6 py-24 scroll-mt-24">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-pink-500 font-bold mb-3">Our Services</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">Interior · Exterior · Full detail</h2>
          </div>
          <p className="text-zinc-400 max-w-md text-sm">Tap any service for full pricing, package comparison, and our exact step-by-step procedure.</p>
        </div>
        <div className="grid gap-5 md:grid-cols-3">
          {SERVICE_CARDS.map((s, i) => (
            <Link key={s.slug} to={`/services/${s.slug}`} {...nt} className="group relative overflow-hidden rounded-2xl border border-white/10 hover:border-pink-500/50 bg-[#121212] transition-all pc-fade-up" style={{animationDelay:`${i*70}ms`}} data-testid={`service-card-${s.slug}`}>
              <div className="h-52 overflow-hidden">
                <img src={s.hero} alt={s.name} style={{ objectPosition: s.heroPos || "center" }} className="w-full h-full object-cover opacity-65 group-hover:opacity-85 group-hover:scale-105 transition-all duration-500" />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold">{s.name}</h3>
                <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{s.tagline}</p>
                <p className="mt-3 text-pink-500 font-bold">{s.range}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm text-pink-500 font-semibold">View service <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" /></span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Add-ons & fees (dedicated section) */}
      <section className="max-w-7xl mx-auto px-6 pb-4">
        <div className="mb-8">
          <p className="text-xs tracking-[0.3em] uppercase text-pink-500 font-bold mb-3">Add-ons & Fees</p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">Four ways to customize your detail</h2>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {ADDON_CARDS.map((c) => (
            <Link key={c.key} to={`/add-ons/${c.key}`} {...nt} className="group relative overflow-hidden rounded-2xl border border-white/10 hover:border-pink-500/50 bg-[#121212] transition-all" data-testid={`home-addon-${c.key}`}>
              <div className="h-36 overflow-hidden"><img src={c.img} alt={c.title} className="w-full h-full object-cover opacity-55 group-hover:opacity-80 group-hover:scale-105 transition-all duration-500" /></div>
              <div className="p-5"><h3 className="text-lg font-bold">{c.title}</h3><p className="text-sm text-zinc-400 mt-1 leading-relaxed">{c.sub}</p></div>
            </Link>
          ))}
        </div>
      </section>

      {/* Explore more (picture cards) */}
      <section className="max-w-7xl mx-auto px-6 pb-12">
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-8">Explore more</h2>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {EXPLORE_CARDS.map((c, i) => (
            <Link key={c.title} to={c.to} {...nt} className="group relative overflow-hidden rounded-2xl border border-white/10 hover:border-pink-500/50 bg-[#121212] transition-all" data-testid={`explore-card-${i}`}>
              <div className="h-36 overflow-hidden">
                <img src={c.img} alt={c.title} className="w-full h-full object-cover opacity-55 group-hover:opacity-80 group-hover:scale-105 transition-all duration-500" />
              </div>
              <div className="p-5">
                <h3 className="text-lg font-bold">{c.title}</h3>
                <p className="text-sm text-zinc-400 mt-1 leading-relaxed">{c.sub}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#121212] p-10 md:p-16 text-center">
          <div className="pc-glow w-[400px] h-[400px] top-0 left-1/2 -translate-x-1/2" />
          <div className="relative">
            <div className="flex justify-center gap-1 mb-5">{[...Array(5)].map((_,i)=><Star key={i} size={20} className="fill-pink-500 text-pink-500" />)}</div>
            <h2 className="text-3xl md:text-5xl font-extrabold tracking-tighter max-w-2xl mx-auto">Ready for a car that turns heads?</h2>
            <p className="text-zinc-400 mt-4 max-w-xl mx-auto">Book your mobile detail today. Pay a 30% deposit to lock your slot, or pay in full and save 5% on your next detail.</p>
            <Link to="/book" {...nt} className="mt-8 inline-flex bg-pink-600 hover:bg-pink-500 text-white font-semibold rounded-full px-10 py-4 transition-colors active:scale-95" data-testid="cta-book-btn">Book Now</Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
